import { useMediaQuery, useTheme } from '@mui/material';
import { FC, ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { scrollToHash, getHeaderHeight } from '../../utils/scrollToHash';

interface SmoothLinkProps {
  to: string;
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
}

/**
 * 스무스 스크롤이 적용된 링크 컴포넌트
 * - #가 있으면 해당 위치로 스무스 스크롤
 * - #가 없으면 페이지 상단으로 스무스 스크롤
 */
const SmoothLink: FC<SmoothLinkProps> = ({
  to,
  children,
  style,
  className,
  onClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const hasHash = to.includes('#');

  // Parse path and hash from the `to` prop
  const [targetPath, hash] = to.split('#');
  const targetPathNormalized = targetPath || '/';
  const isSamePage = location.pathname === targetPathNormalized;

  const handleHashClick = (e: React.MouseEvent) => {
    // Allow modifier keys for new tab behavior
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
      return;
    }

    e.preventDefault();
    onClick?.();

    if (isSamePage) {
      if (hash) {
        // Same page with hash: update hash and scroll to element
        window.history.pushState(null, '', `#${hash}`);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
        scrollToHash(hash, getHeaderHeight(isXs));
      } else {
        // Same page without hash: scroll to top
        window.history.pushState(null, '', targetPathNormalized);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Different page: navigate first, then scroll after page loads
      navigate(to);
      // useHashScroll hook will handle scrolling on the target page
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Allow modifier keys for new tab behavior
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    onClick?.();
    navigate(to);
    // Wait for navigation to complete before scrolling
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  if (hasHash) {
    return (
      <a
        href={to}
        style={style}
        className={className}
        onClick={handleHashClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} style={style} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
};

export default SmoothLink;
